import {
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { cn } from '@tailoring-crm/ui';

/**
 * The six-digit code, as six boxes.
 *
 * Everything here exists because of how the code actually arrives: in a mail app the person is
 * reading on the same phone they are typing on, one-handed, in a hurry.
 *
 *  - **Autofill.** The first box carries `autocomplete="one-time-code"`, which is what makes
 *    iOS offer the code above the keyboard and Android fill it outright. When the platform
 *    fills it, the whole code lands in ONE input — so a multi-character value is spread across
 *    the boxes rather than truncated, which is the bug every hand-rolled OTP field has.
 *  - **Paste.** Same path: copying the code out of the mail and pasting into any box fills
 *    all six, digits only, ignoring any spaces that came with it.
 *  - **Auto-advance and auto-retreat.** Typing moves forward; Backspace on an empty box moves
 *    back and clears the previous one, which is what a person expects when they mistyped the
 *    third digit and are already on the fourth.
 *  - **A numeric keypad**, via `inputMode`, because a QWERTY keyboard for six digits is a
 *    thumb-sized insult.
 *
 * The value is owned by the parent — this component holds no state of its own, so a form
 * reset, a server rejection or an autofill all move it the same way.
 */
export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired when the last box is filled, so the form can submit without a tap. */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  /** Labels the group for a screen reader; each box is labelled by position. */
  label: string;
  digitLabel: (position: number) => string;
  autoFocus?: boolean;
}

const DIGITS_ONLY = /\D/g;

export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  invalid = false,
  label,
  digitLabel,
  autoFocus = false,
}: OtpInputProps): ReactElement {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus();
  }, [autoFocus]);

  const commit = (next: string): void => {
    const clean = next.replace(DIGITS_ONLY, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  };

  const focusBox = (index: number): void => {
    inputs.current[Math.max(0, Math.min(length - 1, index))]?.focus();
  };

  const handleInput = (index: number, raw: string): void => {
    const digits = raw.replace(DIGITS_ONLY, '');
    if (digits.length === 0) {
      // A deletion inside a box.
      commit(value.slice(0, index) + value.slice(index + 1));
      return;
    }

    // One box can receive the WHOLE code — that is what platform autofill does — so the value
    // is written from this position onwards rather than into this box alone.
    const next = (value.slice(0, index) + digits + value.slice(index + digits.length)).slice(
      0,
      length,
    );
    commit(next);
    focusBox(index + digits.length);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace' && value[index] === undefined && index > 0) {
      event.preventDefault();
      commit(value.slice(0, index - 1));
      focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    const pasted = event.clipboardData.getData('text');
    if (!pasted) return;
    event.preventDefault();
    commit(pasted);
    focusBox(pasted.replace(DIGITS_ONLY, '').length);
  };

  return (
    <div role="group" aria-label={label} className="flex justify-between gap-2">
      {Array.from({ length }, (_unused, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          // `text` rather than `number`: a number input strips leading zeros, offers spinners
          // and, on some Androids, refuses a paste.
          type="text"
          inputMode="numeric"
          // Only the first box asks for the code, or the browser fills six boxes with six copies.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          // A single box must still accept a whole pasted or autofilled code.
          maxLength={length}
          value={value[index] ?? ''}
          disabled={disabled}
          aria-label={digitLabel(index + 1)}
          aria-invalid={invalid}
          onChange={(event) => handleInput(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
          className={cn(
            'h-14 w-full min-w-0 rounded-md border border-input bg-background text-center text-2xl font-semibold tabular-nums ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50',
            invalid && 'border-destructive focus-visible:ring-destructive',
          )}
        />
      ))}
    </div>
  );
}
